import React, { useMemo } from "react";
import { useConsole } from "../lib/console";
import { num, timeAgo } from "../lib/format";
import { Button, Card, SevBadge, cx } from "../components/ui";
import { Icon } from "../components/Icon";
import { WorldMap, aggregateSources } from "../components/GeoMap";
import { AttackTypesCard, CountriesCard, PortalPresence, SeverityCard, TrafficCard, VulnerabilityTable } from "../components/WebsiteWatch";
import "./Basic.css";

// Plain-language wording for each attack type, for non-specialist audiences.
const PLAIN = {
  brute_force: "tried to guess a password",
  brute_force_attempt: "failed to sign in",
  port_scan: "scanned the network for open doors",
  sql_injection: "tried to break into the database",
  xss: "tried to inject code into a web page",
  ddos: "flooded the network with traffic",
  botnet_ddos: "flooded the network with traffic",
  malware: "sent malicious software",
  malware_c2: "contacted a malware control server",
  ransomware: "showed ransomware behaviour",
  phishing: "sent a fake login lure",
  data_exfiltration: "tried to copy data out",
  privilege_escalation: "tried to gain admin rights",
  lateral_movement: "tried to move between machines",
  anomalous_traffic: "behaved unusually",
  web_attack: "attacked a web application",
  honeypot: "walked into the decoy admin page",
};
const plain = (t) => PLAIN[String(t || "").toLowerCase()] || "did something suspicious";

const STATUS = {
  ok: { icon: "shield", title: "Your network is safe" },
  medium: { icon: "bell", title: "Some activity needs a look" },
  critical: { icon: "shieldAlert", title: "Attention needed" },
};

export default function Basic() {
  const c = useConsole();
  const { level, stats, threats, blockedIPs, connected, portal } = c;
  const status = STATUS[level.tone] || STATUS.ok;
  const sources = useMemo(() => aggregateSources(threats, 40), [threats]);
  const recent = useMemo(() => (threats || []).filter((t) => t.is_threat !== false).slice(0, 10), [threats]);

  const figures = [
    { label: "Customers online", value: portal?.active?.length ?? 0, hint: "Signed in to the bank site now" },
    { label: "Events checked", value: stats?.total_events, hint: "Connections and sign-ins analysed" },
    { label: "Threats found", value: stats?.total_threats, hint: "Events that looked like an attack" },
    { label: "Attackers blocked", value: blockedIPs.size, hint: "Sources currently cut off" },
  ];

  return (
    <div className="basic">
      <section className={cx("basic-status", `tone-${connected ? level.tone : "info"}`)}>
        <span className="basic-status-icon"><Icon name={connected ? status.icon : "wifi"} size={26} /></span>
        <div>
          <h1>{connected ? status.title : "Connecting to live data…"}</h1>
          <p>{connected ? level.msg : "Figures will appear as soon as the live stream connects."}</p>
        </div>
      </section>

      <div className="basic-figures">
        {figures.map((f) => (
          <div key={f.label} className="basic-figure">
            <span>{f.label}</span>
            <strong className="tnum">{f.value == null ? "—" : num(f.value)}</strong>
            <small>{f.hint}</small>
          </div>
        ))}
      </div>

      <div className="basic-row basic-row-wide">
        <PortalPresence />
        <Card
          title="Live threat feed"
          subtitle="Newest first. Block a source to cut it off, release it to let it back in."
          icon="radar"
          flush
        >
          {!recent.length ? (
            <p className="basic-empty">Nothing suspicious yet. New activity appears here as it happens.</p>
          ) : (
            <ul className="basic-feed">
              {recent.map((t, i) => {
                const blocked = blockedIPs.has(t.source_ip);
                return (
                  <li key={`${t.source_ip}-${t.timestamp}-${i}`}>
                    <button type="button" className="basic-feed-main" onClick={() => c.openDetection(t)}>
                      <span className="basic-feed-text">
                        <strong>Someone {t.country ? `in ${t.country}` : "online"} {plain(t.attack_type)}</strong>
                        <small><span className="mono">{t.source_ip}</span> · {timeAgo(t.timestamp)}</small>
                      </span>
                    </button>
                    <SevBadge sev={t.severity} />
                    <span className={cx("basic-state", blocked && "is-blocked")}>{blocked ? "Blocked" : "Active"}</span>
                    {t.source_ip &&
                      (blocked ? (
                        <Button size="sm" variant="ghost" onClick={() => c.allowIP(t.source_ip)}>Release</Button>
                      ) : (
                        <Button size="sm" variant="danger" onClick={() => c.blockIP(t.source_ip)}>Block</Button>
                      ))}
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>

      <div className="basic-row">
        <TrafficCard />
        <SeverityCard />
      </div>

      <div className="basic-row">
        <AttackTypesCard />
        <CountriesCard />
      </div>

      <VulnerabilityTable />

      <Card title="Where attacks come from" subtitle="Shaded countries are sending attacks right now. Select a point to see who it is." icon="globe">
        <WorldMap sources={sources} onSelect={(ip) => c.openProfile(ip)} ariaLabel="World map of attack origins by country" />
      </Card>

      <div className="basic-more">
        <span>Want the full analyst tools: detections, investigations, models and reports?</span>
        <Button variant="primary" iconRight="arrowRight" onClick={() => c.setMode("advanced")}>Switch to Advanced</Button>
      </div>
    </div>
  );
}
